import React, { useMemo, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { useUser } from "@/contexts";
import { useCollectionData } from "react-firebase-hooks/firestore";
import { chatService } from "@/services";
import { slugify } from "@/utils";
import {
  Avatar,
  Badge,
  Header,
  Navbar,
  ModalAlert,
  HeaderMenuItem,
} from "@/components";
import {
  HomeIcon,
  MessageIcon,
  SearchIcon,
  HeartIcon,
  SettingsIcon,
  PaymentsIcon,
  AffiliateIcon,
  HelpIcon,
  LogoutIcon,
  PremiumIcon,
} from "@/svgs";

const AppLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData, logout } = useUser();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isAlertOpen, setIsAlertOpen] = useState(false);

  const [conversations] = useCollectionData(
    userData?.uid ? chatService.getMyConversationsQuery(userData.uid) : null,
  );

  const unreadMessages = useMemo(() => {
    if (!conversations || typeof userData?.uid !== "string") return 0;

    const currentUid: string = userData.uid;

    return conversations.reduce((acc: number, conv: any) => {
      const count =
        conv.unreadCount &&
        typeof conv.unreadCount === "object" &&
        currentUid in conv.unreadCount
          ? conv.unreadCount[currentUid] || 0
          : 0;
      return acc + count;
    }, 0);
  }, [conversations, userData?.uid]);

  const profileImageUrl =
    userData?.profileImage &&
    (typeof userData.profileImage === "string"
      ? userData.profileImage
      : userData.profileImage.approved !== false
        ? userData.profileImage.url
        : null);
  const profileInitials = userData?.username
    ?.trim()
    .split(/\s+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const handleLogout = async (closeNav: () => void) => {
    if (!window.confirm("Deseja mesmo sair da sua conta?")) return;
    closeNav();
    try {
      await logout();
      navigate("/", { replace: true });
    } catch (e) {
      console.error(e);
    }
  };

  const menuItems: HeaderMenuItem[] = [
    {
      label: "Início",
      icon: <HomeIcon />,
      isActive: location.pathname === "/inicio",
      action: (close: () => void) => {
        close();
        navigate("/inicio");
      },
    },
    {
      label: "Mensagens",
      icon: <MessageIcon />,
      isActive: location.pathname === "/mensagens",
      badge: unreadMessages ? <Badge number={unreadMessages} /> : null,
      action: (close: () => void) => {
        close();
        navigate("/mensagens");
      },
    },
    {
      label: "Busca",
      icon: <SearchIcon />,
      isActive: location.pathname === "/buscar",
      action: (close: () => void) => {
        close();
        navigate("/buscar");
      },
    },
    {
      label: "Interesses",
      icon: <HeartIcon />,
      isActive: location.pathname === "/interesses",
      action: (close: () => void) => {
        close();
        navigate("/interesses");
      },
    },
    {
      label: "Configurações e atividade",
      icon: <SettingsIcon />,
      mobileOnly: true,
      action: (close: () => void) => {
        close();
        navigate("/configuracoes");
      },
    },
    {
      label: "Meus Pagamentos",
      icon: <PaymentsIcon />,
      mobileOnly: true,
      action: (close: () => void) => {
        close();
        navigate("/pagamentos");
      },
    },
    {
      label: "Minhas Indicações",
      icon: <AffiliateIcon />,
      mobileOnly: true,
      action: (close: () => void) => {
        close();
        navigate("/indicacoes");
      },
    },
    {
      label: "Como Funciona",
      icon: <HelpIcon />,
      mobileOnly: true,
      action: (close: () => void) => {
        close();
        navigate("/como-funciona");
      },
    },
    {
      label: "Sair",
      icon: <LogoutIcon />,
      mobileOnly: true,
      action: (close: () => void) => handleLogout(close),
    },
  ];

  return (
    <div className="app-shell min-h-screen flex flex-col">
      <Header
        menuItems={menuItems}
        mobileProfileCard={
          <div className="header-card">
            <Avatar src={profileImageUrl || ""} alt={userData?.username} />
            <div className="header-card-user">
              <strong className="header-card-name">{`Olá, ${userData?.username}`}</strong>
              <button
                className="header-card-link"
                onClick={() =>
                  navigate("/perfil/" + slugify(userData?.username!))
                }
              >
                Ver meu Perfil
              </button>
            </div>
          </div>
        }
        rightActions={
          <>
            {!userData?.planInfo?.active && (
              <button
                type="button"
                className="btn-premium"
                onClick={() => navigate("/planos")}
              >
                <PremiumIcon />{" "}
                <span className="btn-premium-text">Seja Premium</span>
              </button>
            )}

            <div className="navbar-anchor">
              <button
                type="button"
                className="profile-btn"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              >
                {profileImageUrl ? (
                  <Avatar src={profileImageUrl} alt={userData?.username} />
                ) : (
                  <span className="avatar-fallback">{profileInitials}</span>
                )}
                <span className="profile-arrow">▾</span>
              </button>
              <Navbar
                open={isUserMenuOpen}
                onClose={() => setIsUserMenuOpen(false)}
              />
            </div>
          </>
        }
      />

      <main className="app-main flex-1">
        <Outlet />
      </main>

      <ModalAlert
        title="Você precisa ser assinante para enviar mensagens."
        isOpen={isAlertOpen}
        onClose={() => setIsAlertOpen(false)}
        onSubscribe={() => {
          navigate("/planos");
          setIsAlertOpen(false);
        }}
      />
    </div>
  );
};

export default AppLayout;
